/**
 * TanStack Query hooks for organization location management.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createOrganizationLocation,
  deleteOrganizationLocation,
  fetchOrganizationLocations,
  fetchOrganizationLocation,
  fetchLocationVersions,
  updateOrganizationLocation,
} from '../services/organizationApi'
import type {
  CreateLocationData,
  LocationVersion,
  OrganizationLocation,
  OrganizationLocationDetail,
  UpdateLocationData,
} from '../types'
import { useTranslation } from 'react-i18next'

/**
 * Hook for fetching organization locations list.
 */
export function useOrganizationLocations(
  organizationId: number | null,
  options: { enabled?: boolean } = {},
) {
  const { t } = useTranslation('organizations')
  const enabled = options.enabled !== undefined ? options.enabled : true

  return useQuery<OrganizationLocation[], Error>({
    queryKey: ['organization', organizationId, 'locations'],
    queryFn: ({ signal }) => {
      if (organizationId === null) {
        throw new Error(t('useOrganizationLocations.organization_id_is_required'))
      }
      return fetchOrganizationLocations(organizationId, { signal })
    },
    enabled: enabled && organizationId !== null,
    staleTime: 1 * 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000, // 5 minutes
  })
}

/**
 * Hook for fetching a single organization location (for edit page).
 * @param locationType - Optional type (refuge, camping, etc.) to fetch the correct model when IDs overlap.
 */
export function useOrganizationLocation(
  organizationId: number | null,
  locationId: number | null,
  options: { enabled?: boolean; locationType?: string } = {},
) {
  const { t } = useTranslation('organizations')
  const enabled = options.enabled !== undefined ? options.enabled : true

  return useQuery<OrganizationLocationDetail, Error>({
    queryKey: ['organization', organizationId, 'location', locationId, options.locationType],
    queryFn: ({ signal }) => {
      if (organizationId === null || locationId === null) {
        throw new Error(t('useOrganizationLocation.organization_id_and_location_id'))
      }
      return fetchOrganizationLocation(organizationId, locationId, {
        signal,
        locationType: options.locationType,
      })
    },
    enabled: enabled && organizationId !== null && locationId !== null,
    staleTime: 1 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  })
}

/**
 * Hook for creating a new location for an organization.
 */
export function useCreateOrganizationLocation(organizationId: number) {
  const queryClient = useQueryClient()

  return useMutation<OrganizationLocationDetail, Error, CreateLocationData>({
    mutationFn: (data) => createOrganizationLocation(organizationId, data),
    onSuccess: () => {
      // Invalidate locations list to show new location
      queryClient.invalidateQueries({
        queryKey: ['organization', organizationId, 'locations'],
      })
    },
  })
}

/**
 * Hook for updating a location belonging to an organization.
 * @param locationType - 'camping' for camping, etc. Used when IDs can overlap.
 */
export function useUpdateOrganizationLocation(
  organizationId: number,
  locationId: number,
  options?: { locationType?: string },
) {
  const queryClient = useQueryClient()

  return useMutation<OrganizationLocationDetail, Error, UpdateLocationData>({
    mutationFn: (data) =>
      updateOrganizationLocation(organizationId, locationId, data, {
        locationType: options?.locationType,
      }),
    onSuccess: () => {
      // Invalidate locations list to show updated location
      queryClient.invalidateQueries({
        queryKey: ['organization', organizationId, 'locations'],
      })
      // Invalidate location detail if it exists
      queryClient.invalidateQueries({
        queryKey: ['organization', organizationId, 'location', locationId],
      })
      // Invalidate versions to show new version entry
      queryClient.invalidateQueries({
        queryKey: ['organization', organizationId, 'location', locationId, 'versions'],
      })
    },
  })
}

/**
 * Hook for deleting a location belonging to an organization.
 */
export function useDeleteOrganizationLocation(
  organizationId: number,
  locationId: number,
) {
  const queryClient = useQueryClient()

  return useMutation<void, Error, void>({
    mutationFn: () => deleteOrganizationLocation(organizationId, locationId),
    onSuccess: () => {
      // Invalidate locations list to remove deleted location
      queryClient.invalidateQueries({
        queryKey: ['organization', organizationId, 'locations'],
      })
    },
  })
}

/**
 * Hook for fetching location version history.
 */
export function useLocationVersions(
  organizationId: number | null,
  locationId: number | null,
  options: { enabled?: boolean } = {},
) {
  const { t } = useTranslation('organizations')
  const enabled = options.enabled !== undefined ? options.enabled : true

  return useQuery<LocationVersion[], Error>({
    queryKey: ['organization', organizationId, 'location', locationId, 'versions'],
    queryFn: ({ signal }) => {
      if (organizationId === null || locationId === null) {
        throw new Error(t('useLocationVersions.organization_id_and_location_id'))
      }
      return fetchLocationVersions(organizationId, locationId, { signal })
    },
    enabled: enabled && organizationId !== null && locationId !== null,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  })
}
